/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import { traced } from "@workglow/util";
import type {
  DataPortSchemaObject,
  FromSchema,
  TypedArraySchemaOptions,
} from "@workglow/util/schema";
import type { ITabularMigrationApplier } from "../migrations";
import type {
  AutoGeneratedKeys,
  CoveringIndexQueryOptions,
  DeleteSearchCriteria,
  InsertEntity,
  ITabularStorage,
  Page,
  PageRequest,
  QueryOptions,
  SearchCriteria,
  SimplifyPrimaryKey,
  TabularChangePayload,
  TabularEventListener,
  TabularEventName,
  TabularEventParameters,
  TabularSubscribeOptions,
} from "./ITabularStorage";

/**
 * Telemetry wrapper for any ITabularStorage implementation.
 * Creates spans for all storage operations.
 */
export class TelemetryTabularStorage<
  Schema extends DataPortSchemaObject,
  PrimaryKeyNames extends ReadonlyArray<keyof Schema["properties"]>,
  Entity = FromSchema<Schema, TypedArraySchemaOptions>,
  PrimaryKey = SimplifyPrimaryKey<Entity, PrimaryKeyNames>,
  InsertType = InsertEntity<Entity, AutoGeneratedKeys<Schema>>,
> implements ITabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey, InsertType> {
  constructor(
    protected readonly storageName: string,
    protected readonly inner: ITabularStorage<
      Schema,
      PrimaryKeyNames,
      Entity,
      PrimaryKey,
      InsertType
    >
  ) {}

  put(value: InsertType): Promise<Entity> {
    return traced("workglow.storage.tabular.put", this.storageName, () => this.inner.put(value));
  }

  putBulk(values: InsertType[]): Promise<Entity[]> {
    return traced("workglow.storage.tabular.putBulk", this.storageName, () =>
      this.inner.putBulk(values)
    );
  }

  get(key: PrimaryKey): Promise<Entity | undefined> {
    return traced("workglow.storage.tabular.get", this.storageName, () => this.inner.get(key));
  }

  delete(key: PrimaryKey | Entity): Promise<void> {
    return traced("workglow.storage.tabular.delete", this.storageName, () =>
      this.inner.delete(key)
    );
  }

  getAll(options?: QueryOptions<Entity>): Promise<Entity[] | undefined> {
    return traced("workglow.storage.tabular.getAll", this.storageName, () =>
      this.inner.getAll(options)
    );
  }

  deleteAll(): Promise<void> {
    return traced("workglow.storage.tabular.deleteAll", this.storageName, () =>
      this.inner.deleteAll()
    );
  }

  size(): Promise<number> {
    return traced("workglow.storage.tabular.size", this.storageName, () => this.inner.size());
  }

  count(criteria?: SearchCriteria<Entity>): Promise<number> {
    return traced("workglow.storage.tabular.count", this.storageName, () =>
      this.inner.count(criteria)
    );
  }

  deleteSearch(criteria: DeleteSearchCriteria<Entity>): Promise<void> {
    return traced("workglow.storage.tabular.deleteSearch", this.storageName, () =>
      this.inner.deleteSearch(criteria)
    );
  }

  getOffsetPage(offset: number, limit: number): Promise<Entity[] | undefined> {
    return traced("workglow.storage.tabular.getOffsetPage", this.storageName, () =>
      this.inner.getOffsetPage(offset, limit)
    );
  }

  getBulk(keys: readonly PrimaryKey[]): Promise<Entity[]> {
    return traced("workglow.storage.tabular.getBulk", this.storageName, () =>
      this.inner.getBulk(keys)
    );
  }

  getPage(request?: PageRequest<Entity>): Promise<Page<Entity>> {
    return traced("workglow.storage.tabular.getPage", this.storageName, () =>
      this.inner.getPage(request)
    );
  }

  queryPage(
    criteria: SearchCriteria<Entity>,
    request?: PageRequest<Entity>
  ): Promise<Page<Entity>> {
    return traced("workglow.storage.tabular.queryPage", this.storageName, () =>
      this.inner.queryPage(criteria, request)
    );
  }

  query(
    criteria: SearchCriteria<Entity>,
    options?: QueryOptions<Entity>
  ): Promise<Entity[] | undefined> {
    return traced("workglow.storage.tabular.query", this.storageName, () =>
      this.inner.query(criteria, options)
    );
  }

  queryIndex<K extends keyof Entity & string>(
    criteria: SearchCriteria<Entity>,
    options: CoveringIndexQueryOptions<Entity, K>
  ): Promise<Pick<Entity, K>[]> {
    return traced("workglow.storage.tabular.queryIndex", this.storageName, () =>
      this.inner.queryIndex(criteria, options)
    );
  }

  records(pageSize?: number): AsyncGenerator<Entity, void, undefined> {
    return this.inner.records(pageSize);
  }

  pages(pageSize?: number): AsyncGenerator<Entity[], void, undefined> {
    return this.inner.pages(pageSize);
  }

  subscribeToChanges(
    callback: (change: TabularChangePayload<Entity>) => void,
    options?: TabularSubscribeOptions
  ): () => void {
    return this.inner.subscribeToChanges(callback, options);
  }

  withTransaction<T>(fn: (tx: this) => Promise<T>): Promise<T> {
    // Construct a tx-bound telemetry wrapper that forwards to the inner's
    // transaction handle (`innerTx`) — the proxy from the inner's
    // `createTxView`. Passing the outer `this` would re-enter `inner.put()`
    // through the public, mutex-acquiring path and deadlock against the held
    // mutex on single-connection backends, or run on the wrong connection on
    // real `pg.Pool`.
    return traced("workglow.storage.tabular.withTransaction", this.storageName, () =>
      this.inner.withTransaction((innerTx) => {
        const txWrapper = new TelemetryTabularStorage(
          this.storageName,
          innerTx as ITabularStorage<Schema, PrimaryKeyNames, Entity, PrimaryKey, InsertType>
        ) as this;
        return fn(txWrapper);
      })
    );
  }

  setupDatabase(): Promise<void> {
    return this.inner.setupDatabase();
  }

  /**
   * Forwards to the inner storage's applier so callers that wrap a
   * migration-aware storage in TelemetryTabularStorage still observe
   * declared migrations.
   */
  getMigrationApplier(): ITabularMigrationApplier | null {
    const inner = this.inner as unknown as {
      getMigrationApplier?: () => ITabularMigrationApplier | null;
    };
    return inner.getMigrationApplier?.() ?? null;
  }

  destroy(): void {
    return this.inner.destroy();
  }

  [Symbol.dispose](): void {
    return this.inner[Symbol.dispose]();
  }

  [Symbol.asyncDispose](): Promise<void> {
    return this.inner[Symbol.asyncDispose]();
  }

  on<Event extends TabularEventName>(
    name: Event,
    fn: TabularEventListener<Event, PrimaryKey, Entity>
  ): void {
    this.inner.on(name, fn);
  }

  off<Event extends TabularEventName>(
    name: Event,
    fn: TabularEventListener<Event, PrimaryKey, Entity>
  ): void {
    this.inner.off(name, fn);
  }

  emit<Event extends TabularEventName>(
    name: Event,
    ...args: TabularEventParameters<Event, PrimaryKey, Entity>
  ): void {
    this.inner.emit(name, ...args);
  }

  once<Event extends TabularEventName>(
    name: Event,
    fn: TabularEventListener<Event, PrimaryKey, Entity>
  ): void {
    this.inner.once(name, fn);
  }

  waitOn<Event extends TabularEventName>(
    name: Event
  ): Promise<TabularEventParameters<Event, PrimaryKey, Entity>> {
    return this.inner.waitOn(name);
  }
}
